<template>
  <modal
    :show="show"
    @update:show="$emit('update:show', $event)"
    body-classes="p-0"
    modal-classes="modal-dialog-centered modal-lg"
  >
    <card
      type="secondary"
      header-classes="bg-white pb-5"
      body-classes="px-lg-5 py-lg-5"
      class="border-0 mb-0"
    >
      <div class="d-flex align-items-center justify-content-between">
        <h4 class="card-title mb-0">Workflow states</h4>
        <base-button
          v-if="!showForm && !loading"
          size="sm"
          type="primary"
          @click="openAddForm()"
        >
          <i class="tim-icons icon-simple-add mr-1"></i> Add state
        </base-button>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="workflow-state-list mt-3">
        <div v-for="n in 3" :key="'skel-' + n" class="workflow-state-row d-flex align-items-center">
          <span class="skeleton-dot mr-2"></span>
          <span class="skeleton-text flex-grow-1"></span>
          <span class="skeleton-btn"></span>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="states.length === 0 && !showForm" class="text-center mt-4 mb-2">
        <div class="font-icon-detail">
          <img src="/img/pandas/kanban.png" alt="No workflow states" class="empty-state-img empty-state-img--md" />
          <h4 class="mt-3">No workflow states defined</h4>
        </div>
      </div>

      <!-- Draggable state list -->
      <div v-else-if="states.length > 0 && !showForm" class="workflow-state-list mt-3">
        <draggable
          v-model="states"
          @end="reorderStates"
          handle=".drag-handle"
          :animation="200"
          ghost-class="workflow-state-ghost"
        >
          <div
            v-for="state in states"
            :key="state.id"
            class="workflow-state-row d-flex align-items-center"
          >
            <i style="cursor: move" class="tim-icons el-icon-rank drag-handle mr-3"></i>
            <div class="workflow-state-info flex-grow-1">
              <span class="workflow-state-name text-white">
                <b-icon
                  v-if="state.icon"
                  :icon="state.icon"
                  class="mr-1"
                  :style="{ color: state.color }"
                ></b-icon>
                <span
                  v-else
                  class="workflow-color-dot mr-1"
                  :style="{ backgroundColor: state.color }"
                ></span>
                {{ state.name }}
              </span>
              <small v-if="state.description" class="d-block text-truncate" style="max-width: 300px; color: rgba(255, 255, 255, 0.7)">
                {{ state.description }}
              </small>
            </div>
            <el-dropdown trigger="click">
              <base-button link type="primary" size="sm">
                <i style="font-size: large" class="tim-icons el-icon-more"></i>
              </base-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <a class="dropdown-item text-default mb-2" href="#" @click.prevent="editState(state)">
                    <i class="tim-icons text-default icon-pencil mr-2"></i> Edit
                  </a>
                  <hr class="my-1">
                  <a class="dropdown-item text-default" href="#" @click.prevent="archiveState(state)">
                    <i class="tim-icons text-default icon-trash-simple mr-2"></i> Archive
                  </a>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </draggable>

      </div>

      <!-- Inline add/edit form -->
      <div v-if="showForm" class="workflow-state-form mt-3">
        <div class="row">
          <div class="col-md-6">
            <base-input
              required
              label="Name"
              type="text"
              placeholder="e.g., Needs GM assigned"
              v-model="selectedState.name"
              :error="errs.name"
              maxlength="100"
            ></base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <label>Description</label>
            <el-input
              v-model="selectedState.description"
              :rows="2"
              type="textarea"
              placeholder="Optional description"
              maxlength="255"
            ></el-input>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <label class="d-block mb-2">Color</label>
            <div class="color-swatches">
              <div
                v-for="color in colorPresets"
                :key="color"
                class="color-swatch"
                :class="{ 'color-swatch--active': selectedState.color === color }"
                :style="{ backgroundColor: color }"
                @click="selectedState.color = color"
              ></div>
              <el-color-picker
                v-model="selectedState.color"
                size="small"
                class="color-swatch-more"
              ></el-color-picker>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <label class="d-block mb-2">Icon <span class="text-muted font-weight-normal">(optional)</span></label>
            <div class="icon-grid">
              <div
                v-for="icon in iconOptions"
                :key="icon.value"
                class="icon-grid-item"
                :class="{ 'icon-grid-item--active': selectedState.icon === icon.value }"
                @click="selectedState.icon = selectedState.icon === icon.value ? null : icon.value"
                :title="icon.name"
              >
                <b-icon :icon="icon.value"></b-icon>
              </div>
            </div>
            <base-button
              v-if="selectedState.icon"
              link
              size="sm"
              type="default"
              class="mt-1 p-0 text-white"
              @click="selectedState.icon = null"
            >
              Clear icon
            </base-button>
          </div>
        </div>

        <div class="mt-3">
          <base-button size="sm" type="secondary" @click="cancelForm">Cancel</base-button>
          <base-button
            size="sm"
            type="primary"
            class="ml-2"
            @click="saveState"
            :loading="saving"
            :disabled="!selectedState.name || !selectedState.name.trim()"
          >
            {{ isEditing ? 'Update' : 'Create' }}
          </base-button>
        </div>
      </div>

      <div class="row text-center mt-4">
        <div class="col">
          <base-button type="secondary" @click="$emit('update:show', false)">Cancel</base-button>
        </div>
      </div>
    </card>
  </modal>
</template>

<script>
import Modal from '@/components/Modal';
import { API_LOCATION } from '@/api-config';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import { ColorPicker, Dropdown, DropdownMenu, DropdownItem, Input } from 'element-ui';
import draggable from 'vuedraggable';
import { BIcon } from 'bootstrap-vue';

export default {
  name: 'WorkflowStatesModal',
  components: {
    Modal,
    [ColorPicker.name]: ColorPicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Input.name]: Input,
    draggable,
    BIcon
  },
  props: {
    show: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      saving: false,
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      states: [],
      selectedState: {
        name: '',
        description: '',
        color: '#1d8cf8',
        icon: null
      },
      isEditing: false,
      showForm: false,
      errs: { name: '' },
      colorPresets: [
        '#1d8cf8', '#e14eca', '#2dce89', '#fb6340',
        '#f5365c', '#ffd600', '#11cdef', '#8965e0',
        '#525f7f', '#d44872'
      ]
    };
  },
  computed: {
    iconOptions() {
      return [
        'check-circle', 'exclamation-triangle', 'bell', 'calendar',
        'chat', 'lightning', 'envelope', 'lock',
        'cash', 'journal-text', 'file-earmark', 'pencil',
        'puzzle', 'emoji-smile', 'gear', 'person',
        'headset', 'tag', 'alarm', 'trophy',
        'flag', 'star', 'eye', 'clipboard-check'
      ].map(icon => ({ name: icon, value: icon }));
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.showForm = false;
        this.isEditing = false;
        this.getStates();
      }
    }
  },
  methods: {
    async getAuthToken() {
      const user = await Auth.currentAuthenticatedUser();
      return user.signInUserSession.idToken.jwtToken;
    },

    async getStates() {
      this.loading = true;
      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/workflow-states`;

        let response = await axios.get(url, config);
        this.states = response.data;
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.$notify({
            type: 'danger',
            message: 'Failed to load workflow states',
            timeout: 3000
          });
        }
      } finally {
        this.loading = false;
      }
    },

    openAddForm() {
      this.isEditing = false;
      this.selectedState = {
        name: '',
        description: '',
        color: '#1d8cf8',
        icon: null
      };
      this.errs.name = '';
      this.showForm = true;
    },

    editState(state) {
      this.isEditing = true;
      this.selectedState = {
        id: state.id,
        name: state.name,
        description: state.description || '',
        color: state.color || '#1d8cf8',
        icon: state.icon || null
      };
      this.errs.name = '';
      this.showForm = true;
    },

    cancelForm() {
      this.showForm = false;
      this.isEditing = false;
      this.errs.name = '';
    },

    async saveState() {
      if (!this.selectedState.name || !this.selectedState.name.trim()) {
        this.errs.name = 'Name is required';
        return;
      }
      this.errs.name = '';
      this.saving = true;

      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let data = {
          name: this.selectedState.name.trim(),
          description: this.selectedState.description ? this.selectedState.description.trim() : null,
          color: this.selectedState.color || '#1d8cf8',
          icon: this.selectedState.icon || null
        };

        if (this.isEditing) {
          let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/workflow-states/${this.selectedState.id}`;
          await axios.put(url, data, config);
          this.$notify({ type: 'success', message: 'Workflow state updated', timeout: 3000 });
        } else {
          let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/workflow-states`;
          await axios.post(url, data, config);
          this.$notify({ type: 'success', message: 'Workflow state created', timeout: 3000 });
        }

        this.showForm = false;
        this.isEditing = false;
        await this.getStates();
        this.$emit('saved');
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          swal('Error', 'Something went wrong', 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    async archiveState(state) {
      let result = await swal({
        title: 'Are you sure?',
        text: `"${state.name}" will be archived. Bookings in this state will be unlinked.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Archive',
        cancelButtonText: 'Cancel'
      });

      if (!result.value) return;

      this.loading = true;
      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/workflow-states/${state.id}`;

        await axios.delete(url, config);
        this.$notify({ type: 'success', message: 'Workflow state archived', timeout: 3000 });
        await this.getStates();
        this.$emit('saved');
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          swal('Error', 'Something went wrong', 'error');
        }
        this.loading = false;
      }
    },

    async reorderStates() {
      try {
        let token = await this.getAuthToken();
        let config = { headers: { Authorization: token } };
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/workflow-states`;
        let data = { state_ids: this.states.map(s => s.id) };

        await axios.put(url, data, config);
        this.$emit('saved');
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.$router.push('/forbidden');
        } else {
          this.$notify({
            type: 'danger',
            message: 'Failed to save order',
            timeout: 3000
          });
          await this.getStates();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.workflow-state-list {
  max-height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
}

.workflow-state-row {
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  margin-bottom: 6px;
  background: rgba(255, 255, 255, 0.02);
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
}

.workflow-state-ghost {
  opacity: 0.5;
  background: rgba(225, 78, 202, 0.1);
}

.drag-handle {
  color: rgba(255, 255, 255, 0.3);
  font-size: 14px;

  &:hover {
    color: rgba(255, 255, 255, 0.6);
  }
}

.workflow-color-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
}

.workflow-state-icon {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.workflow-state-name {
  font-weight: 600;
  font-size: 14px;
}

.color-swatches {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.15s;

  &:hover {
    transform: scale(1.15);
  }

  &--active {
    border-color: #fff;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.3);
  }
}

.color-swatch-more {
  margin-left: 2px;
}

.workflow-state-form {
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.icon-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.icon-grid-item {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.15s;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
  }

  &--active {
    background: rgba(225, 78, 202, 0.15);
    border-color: #e14eca;
    color: #e14eca;
  }
}

// Skeleton styles
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-dot, .skeleton-text, .skeleton-btn {
  display: inline-block;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.skeleton-dot {
  width: 14px;
  height: 14px;
}

.skeleton-text {
  height: 16px;
  max-width: 140px;
}

.skeleton-btn {
  width: 28px;
  height: 20px;
}

// Light mode
.white-content {
  .workflow-state-row {
    border-color: rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.03);

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  .drag-handle {
    color: rgba(0, 0, 0, 0.25);
    &:hover { color: rgba(0, 0, 0, 0.5); }
  }

  .workflow-state-icon {
    color: rgba(0, 0, 0, 0.6);
  }

  .workflow-state-form {
    border-top-color: rgba(0, 0, 0, 0.1);
  }

  .color-swatch--active {
    border-color: #344675;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
  }

  .icon-grid-item {
    border-color: #ddd;
    color: rgba(0, 0, 0, 0.5);

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: rgba(0, 0, 0, 0.8);
    }

    &--active {
      background: rgba(225, 78, 202, 0.15);
      border-color: #e14eca;
      color: #e14eca;
    }
  }

  .skeleton-dot, .skeleton-text, .skeleton-btn {
    background: linear-gradient(90deg, #e9ecef 25%, #f5f6fa 50%, #e9ecef 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.5s ease-in-out infinite;
  }
}

</style>
